import React, { useState, useEffect } from "react";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";

import { API_URI } from "../../helpers";

function Stat({ label, value, sub, color }) {
  return (
    <Card style={{ marginTop: 0 }}>
      <CardBody style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: color || "#1f2937" }}>{value}</div>
        <div style={{ fontSize: 13, color: "#64748B", fontWeight: 600 }}>{label}</div>
        {sub != null && <div style={{ fontSize: 12, color: "#94A3B8" }}>{sub}</div>}
      </CardBody>
    </Card>
  );
}

/**
 * Un número que puede no existir todavía.
 *
 * Con 0 enviados la API manda `null` en los porcentajes en vez de 0: un "0%" al lado de
 * "0 enviados" se lee como "esto no funciona", y es otra cosa. Acá se respeta esa distinción.
 */
function Pct({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#CBD5E1" }}>—</span>;
  return <span>{value}%</span>;
}

/**
 * Preview de un paso + envío de prueba.
 *
 * El HTML va en un iframe y no inyectado en la página: el mail trae sus propios estilos y,
 * suelto, se mezclaría con los del panel — se vería distinto de como le llega a la gente, que
 * es justo lo que un preview tiene que evitar.
 */
function PreviewDialog({ step, seq, onClose }) {
  const [data, setData] = useState(null);
  const [mail, setMail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!step) return;
    setData(null);
    setMsg(null);
    fetch(`${API_URI}/dashboard/mail-sequence/preview?sequence=${seq}&step=${step.position}`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ error: true }));
  }, [step, seq]);

  function enviarPrueba() {
    setEnviando(true);
    setMsg(null);
    fetch(`${API_URI}/dashboard/mail-sequence/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sequence: seq, step: step.position, email: mail }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        setMsg({
          ok: ok && j.success,
          texto: j.mensaje || j.error || (ok ? "Encolado." : "No se pudo enviar."),
        });
      })
      .catch(() => setMsg({ ok: false, texto: "No se pudo enviar." }))
      .finally(() => setEnviando(false));
  }

  if (!step) return null;

  return (
    <Dialog open={!!step} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ fontSize: 13, color: "#94A3B8" }}>
          Paso {step.position} · día {Math.round(step.after_hours / 24)}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {data && data.subject ? data.subject : step.subject}
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {!data && <div style={{ padding: 20, color: "#64748B" }}>Cargando preview…</div>}
        {data && data.error && (
          <div style={{ padding: 20, color: "#DC2626" }}>No se pudo cargar el preview.</div>
        )}
        {data && data.html && (
          <iframe
            title="preview"
            srcDoc={data.html}
            style={{
              width: "100%",
              height: 420,
              border: "1px solid #E2E8F0",
              borderRadius: 6,
              background: "#fff",
            }}
          />
        )}
        <div style={{ marginTop: 16, fontSize: 12, color: "#94A3B8" }}>
          Los botones del preview están firmados contra un envío inexistente: se ven y se pueden
          inspeccionar, pero no suman clics a las métricas.
        </div>
      </DialogContent>
      <DialogActions style={{ padding: 16, flexWrap: "wrap", gap: 8 }}>
        <TextField
          label="Enviar una prueba a…"
          placeholder="tucorreo@ejemplo.com"
          value={mail}
          onChange={(e) => setMail(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        <Button
          color="success"
          onClick={enviarPrueba}
          disabled={enviando || !mail.trim()}
        >
          {enviando ? "Enviando…" : "Enviar prueba"}
        </Button>
        <Button color="info" onClick={onClose}>
          Cerrar
        </Button>
        {msg && (
          <div
            style={{
              width: "100%",
              fontSize: 13,
              color: msg.ok ? "#16A34A" : "#DC2626",
              fontWeight: 600,
            }}
          >
            {msg.texto}
            {msg.ok && " (el outbox se drena cada 2 minutos)"}
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function MailSequenceDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [seq, setSeq] = useState("clasegratis");

  function load() {
    setLoading(true);
    fetch(`${API_URI}/dashboard/mail-sequence?sequence=${seq}`)
      .then((r) => r.json())
      .then(setD)
      .finally(() => setLoading(false));
  }
  // Recarga al cambiar de secuencia. El preview abierto se cierra: quedaría mostrando el
  // paso de la secuencia anterior contra los datos de la nueva.
  useEffect(() => {
    setPreview(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq]);

  if (!d) return <div style={{ padding: 24 }}>{loading ? "Cargando…" : "Sin datos."}</div>;

  const steps = d.steps || [];
  const tot = steps.reduce(
    (a, s) => ({
      sent: a.sent + (s.sent || 0),
      opened: a.opened + (s.opened || 0),
      clicked: a.clicked + (s.clicked || 0),
      pay: a.pay + (s.reached_pay || 0),
      bought: a.bought + (s.purchased || 0),
      pending: a.pending + (s.pending || 0),
      failed: a.failed + (s.failed || 0),
    }),
    { sent: 0, opened: 0, clicked: 0, pay: 0, bought: 0, pending: 0, failed: 0 }
  );

  const th = { fontWeight: 700, color: "#334155", fontSize: 13 };
  const td = { fontSize: 14 };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>Secuencia de mails</h4>
          <select
            value={seq}
            onChange={(e) => setSeq(e.target.value)}
            style={{
              fontSize: 14,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #CBD5E1",
              background: "#fff",
            }}
          >
            {(d.sequences || [{ key: seq, label: seq }]).map((s) => (
              <option key={s.key} value={s.key}>
                {s.label} {s.enabled ? "•" : "○"}
              </option>
            ))}
          </select>
          <span
            style={{
              background: d.enabled ? "#16A34A" : "#DC2626",
              color: "#fff",
              borderRadius: 6,
              padding: "2px 10px",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {d.enabled ? "ENCENDIDA" : "APAGADA"}
          </span>
          {/* Son DOS llaves y hacen falta las dos. Un cartel que dice «apagada» sin decir
              cuál de las dos falta manda a revisar el lugar equivocado. */}
          {!d.enabled && (
            <span style={{ fontSize: 11, color: "#94A3B8" }}>
              {d.switch_global === false
                ? "la mensajería está apagada para todas (MAIL_SEQUENCE_ENABLED)"
                : "esta secuencia está apagada · mailseq:toggle " + d.sequence + " --on"}
            </span>
          )}
          <span style={{ fontSize: 13, color: "#64748B" }}>
            {d.list_size} personas en la lista
          </span>
          <Button color="info" size="sm" onClick={load} disabled={loading}>
            {loading ? "..." : "Actualizar"}
          </Button>
        </div>
      </GridItem>

      <GridItem xs={12} sm={6} md={2}>
        <Stat label="Enviados" value={tot.sent} sub={tot.pending ? `${tot.pending} en cola` : null} />
      </GridItem>
      <GridItem xs={12} sm={6} md={2}>
        <Stat
          label="Abrieron"
          value={tot.opened}
          sub="inflado por Gmail"
          color="#0EA5E9"
        />
      </GridItem>
      <GridItem xs={12} sm={6} md={2}>
        <Stat label="Clickearon" value={tot.clicked} sub="el dato que vale" color="#7C3AED" />
      </GridItem>
      <GridItem xs={12} sm={6} md={2}>
        <Stat label="Llegaron al pago" value={tot.pay} color="#F59E0B" />
      </GridItem>
      <GridItem xs={12} sm={6} md={2}>
        <Stat label="Compraron" value={tot.bought} color="#16A34A" />
      </GridItem>
      <GridItem xs={12} sm={6} md={2}>
        <Stat
          label="Fallaron"
          value={tot.failed}
          color={tot.failed ? "#DC2626" : "#94A3B8"}
        />
      </GridItem>

      <GridItem xs={12}>
        <Card>
          <CardBody>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={th}>Paso</TableCell>
                  <TableCell style={th}>Asunto</TableCell>
                  <TableCell style={th} align="right">Enviados</TableCell>
                  <TableCell style={th} align="right">Abrieron</TableCell>
                  <TableCell style={th} align="right">Clic</TableCell>
                  <TableCell style={th} align="right">Al pago</TableCell>
                  <TableCell style={th} align="right">Compraron</TableCell>
                  <TableCell style={th} align="center">Mail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {steps.map((s) => (
                  <TableRow key={s.position} style={{ opacity: s.active ? 1 : 0.45 }}>
                    <TableCell style={td}>
                      <strong>{s.position}</strong>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        día {Math.round(s.after_hours / 24)}
                        {!s.active && " · inactivo"}
                      </div>
                    </TableCell>
                    <TableCell style={td}>{s.subject}</TableCell>
                    <TableCell style={td} align="right">
                      {s.sent}
                      {s.pending > 0 && (
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>{s.pending} en cola</div>
                      )}
                      {s.failed > 0 && (
                        <div style={{ fontSize: 12, color: "#DC2626" }}>{s.failed} fallaron</div>
                      )}
                    </TableCell>
                    <TableCell style={td} align="right">
                      {s.opened}
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        <Pct value={s.open_rate} />
                      </div>
                    </TableCell>
                    <TableCell style={td} align="right">
                      {s.clicked}
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        <Pct value={s.click_rate} />
                      </div>
                    </TableCell>
                    <TableCell style={td} align="right">{s.reached_pay}</TableCell>
                    <TableCell style={td} align="right">
                      <strong style={{ color: s.purchased ? "#16A34A" : "#94A3B8" }}>
                        {s.purchased}
                      </strong>
                    </TableCell>
                    <TableCell style={td} align="center">
                      <Button color="info" size="sm" onClick={() => setPreview(s)}>
                        Ver / probar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div style={{ marginTop: 14, fontSize: 12, color: "#94A3B8", lineHeight: 1.6 }}>
              {d.nota_aperturas}
              <br />
              Las aperturas y los clics se cuentan <strong>por persona</strong>, no por evento: si
              alguien abre el mismo mail cinco veces sigue siendo uno.
              {!d.enabled && (
                <>
                  <br />
                  La secuencia está apagada: no se envía nada hasta que{" "}
                  <code>MAIL_SEQUENCE_ENABLED</code> pase a true.
                </>
              )}
            </div>
          </CardBody>
        </Card>
      </GridItem>

      <PreviewDialog step={preview} seq={seq} onClose={() => setPreview(null)} />
    </GridContainer>
  );
}
